import { Platform } from '@/utils/detectPlatform';
import { isNullish } from '@/utils/isNullish';

// `x-safari-https://` does nothing before iOS 17 (https://stackoverflow.com/q/76888215).
const X_SAFARI_MIN_IOS_VERSION = 17;

/**
 * The URL that asks the host app to reopen `href` in the system browser, or
 * `null` when there is no such scheme and only copying the link is left.
 * With `null` the notice shows the host-menu steps and the copy-link field instead of the open button.
 * Only LINE and the Google app are known to follow these URLs; every other host gets `null`,
 * as does the Google app on iOS before 17 or on an iOS version the UA does not tell.
 */
export const buildExternalBrowserUrl = ({ os, osMajorVersion, inApp }: Platform, href: string): string | null => {
  if (inApp !== 'line' && inApp !== 'google') {
    return null;
  }

  const url = new URL(href);

  if (inApp === 'line') {
    url.searchParams.set('openExternalBrowser', '1');
    return url.toString();
  }

  const scheme = url.protocol.replace(':', '');
  const pathAndQuery = `${url.pathname}${url.search}`;

  if (os === 'android') {
    // The fragment is dropped: `#Intent;…` takes its place in an intent URL.
    return `intent://${url.host}${pathAndQuery}#Intent;scheme=${scheme};package=com.android.chrome;end`;
  }
  if (os === 'ios' && !isNullish(osMajorVersion) && osMajorVersion >= X_SAFARI_MIN_IOS_VERSION) {
    return `x-safari-${scheme}://${url.host}${pathAndQuery}${url.hash}`;
  }
  return null;
};
